import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// MarkdownRender组件 - 用于预览Markdown文本
// @param {string} text - 需要渲染的Markdown文本内容
// @param {string} className - 自定义CSS类名
// @param {Object} style - 自定义内联样式
const MarkdownRender = ({ text, className = '', style = {} }) => {
  // 如果没有提供text，渲染一个提示信息
  if (!text || text.trim() === '') {
    return (
      <div className={`markdown-render ${className}`} style={style}>
        <div className="empty-message">
          请输入Markdown文本进行预览
        </div>
      </div>
    );
  }

  return (
    <div className={`markdown-render ${className}`} style={style}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // 自定义渲染组件，优化预览效果
          h1: ({ node, ...props }) => (
            <h1 style={{ 
              color: '#000', 
              marginTop: '1.5rem',
              marginBottom: '1rem',
              paddingBottom: '0.5rem'
            }} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 style={{ 
              color: '#000', 
              marginTop: '1.2rem',
              marginBottom: '0.8rem',
              paddingBottom: '0.4rem'
            }} {...props} />
          ),
          h3: ({ node, ...props }) => {
            // 生成id，用于导航跳转
            const id = props.children?.[0]?.toLowerCase().replace(/\s+/g, '-') || `heading-${Math.random().toString(36).substr(2, 9)}`;
            return (
              <h3 id={id} style={{ 
                color: '#000', 
                marginTop: '1rem',
                marginBottom: '0.6rem'
              }} {...props} />
            );
          },
          h4: ({ node, ...props }) => {
            // 生成id，用于导航跳转
            const id = props.children?.[0]?.toLowerCase().replace(/\s+/g, '-') || `heading-${Math.random().toString(36).substr(2, 9)}`;
            return (
              <h4 id={id} style={{ 
                color: '#000', 
                marginTop: '0.8rem',
                marginBottom: '0.4rem'
              }} {...props} />
            );
          },
          h5: ({ node, ...props }) => {
            // 生成id，用于导航跳转
            const id = props.children?.[0]?.toLowerCase().replace(/\s+/g, '-') || `heading-${Math.random().toString(36).substr(2, 9)}`;
            return (
              <h5 id={id} style={{ 
                color: '#000', 
                marginTop: '0.6rem',
                marginBottom: '0.3rem'
              }} {...props} />
            );
          },
          h6: ({ node, ...props }) => {
            // 生成id，用于导航跳转
            const id = props.children?.[0]?.toLowerCase().replace(/\s+/g, '-') || `heading-${Math.random().toString(36).substr(2, 9)}`;
            return (
              <h6 id={id} style={{ 
                color: '#000', 
                marginTop: '0.5rem',
                marginBottom: '0.2rem'
              }} {...props} />
            );
          },
          p: ({ node, ...props }) => (
            <p style={{ 
              marginBottom: '0.8rem',
              lineHeight: '1.7'
            }} {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul style={{ 
              marginBottom: '0.8rem',
              paddingLeft: '1.5rem'
            }} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol style={{ 
              marginBottom: '0.8rem',
              paddingLeft: '1.5rem'
            }} {...props} />
          ),
          li: ({ node, ...props }) => (
            <li style={{ 
              marginBottom: '0.3rem'
            }} {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote style={{ 
              borderLeft: '4px solid #1890ff',
              paddingLeft: '1rem',
              marginBottom: '0.8rem',
              color: '#595959',
              fontStyle: 'italic'
            }} {...props} />
          ),
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <pre style={{ 
                backgroundColor: '#f6f8fa',
                borderRadius: '4px',
                padding: '1rem',
                overflowX: 'auto',
                marginBottom: '0.8rem'
              }}>
                <code className={className} style={{ 
                  fontFamily: 'Consolas, Monaco, Courier New, monospace',
                  fontSize: '0.9rem',
                  backgroundColor: 'transparent'
                }} {...props}>
                  {String(children).replace(/\n$/, '')}
                </code>
              </pre>
            ) : (
              <code className={className} style={{ 
                backgroundColor: '#f6f8fa',
                padding: '0.2rem 0.4rem',
                borderRadius: '3px',
                fontFamily: 'Consolas, Monaco, Courier New, monospace',
                fontSize: '0.9rem'
              }} {...props}>
                {children}
              </code>
            );
          },
          table: ({ node, ...props }) => (
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '0.8rem',
              overflowX: 'auto',
              display: 'block'
            }} {...props} />
          ),
          th: ({ node, ...props }) => (
            <th style={{ 
              border: '1px solid #e8e8e8',
              padding: '0.5rem',
              textAlign: 'left',
              backgroundColor: '#fafafa',
              fontWeight: 'bold'
            }} {...props} />
          ),
          td: ({ node, ...props }) => (
            <td style={{ 
              border: '1px solid #e8e8e8',
              padding: '0.5rem',
              textAlign: 'left'
            }} {...props} />
          ),
          input: ({ node, ...props }) => (
            <input style={{ 
              marginRight: '0.5rem'
            }} {...props} />
          ),
          img: ({ node, ...props }) => (
            <img style={{ 
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '1rem auto',
              borderRadius: '4px'
            }} {...props} />
          ),
          a: ({ node, ...props }) => (
            <a style={{ 
              color: '#1890ff',
              textDecoration: 'none'
            }} {...props} />
          ),
          hr: ({ node, ...props }) => (
            <hr style={{ 
              border: 'none',
              borderTop: '1px solid #e8e8e8',
              margin: '1.5rem 0'
            }} {...props} />
          ),
          del: ({ node, ...props }) => (
            <del style={{ 
              textDecoration: 'line-through',
              color: '#999'
            }} {...props} />
          )
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRender;